import { IAnswerState, IReducedAnswerState } from '@/modules/SurveyForm';
import { useCallback, useState } from 'react';

export interface ExporterFavorite {
  id: string;
  name: string;
  values: IReducedAnswerState;
  createdAt: string;
}

interface UseExporterFavoritesProps {
  screenName: string;
}

const STORAGE_KEY_PREFIX = 'exporter_favorites_';

// Question types that should be excluded from favorites (date fields)
const EXCLUDED_QUESTION_TYPES = ['date', 'picker_date', 'date_range', 'time_picker'];

const getStorageKey = (screenName: string) =>
  `${STORAGE_KEY_PREFIX}${screenName}`;

// Helper to filter out date fields from values
const filterDateFields = (values: IReducedAnswerState): IReducedAnswerState => {
  const filtered: IReducedAnswerState = {};
  Object.entries(values).forEach(([key, value]) => {
    if (value && !EXCLUDED_QUESTION_TYPES.includes(value.questionType || '')) {
      filtered[key] = value;
    }
  });
  return filtered;
};

// Helper to convert Date objects to ISO strings for storage
const serializeValues = (values: IReducedAnswerState): Record<string, any> => {
  const serialized: Record<string, any> = {};
  Object.entries(values).forEach(([key, value]) => {
    if (value) {
      serialized[key] = {
        ...value,
        answeredAt: value.answeredAt instanceof Date 
          ? value.answeredAt.toISOString() 
          : value.answeredAt,
      };
    }
  });
  return serialized;
};

// Helper to convert ISO strings back to Date objects
const deserializeValues = (values: IReducedAnswerState): IReducedAnswerState => {
  const deserialized: IReducedAnswerState = {};
  Object.entries(values).forEach(([key, value]) => {
    if (value) {
      deserialized[key] = {
        ...value,
        answeredAt: typeof value.answeredAt === 'string' 
          ? new Date(value.answeredAt) 
          : value.answeredAt,
      };
    }
  });
  return deserialized;
};

export function useExporterFavorites({ screenName }: UseExporterFavoritesProps) {
  const storageKey = getStorageKey(screenName);

  const loadFavorites = useCallback((): ExporterFavorite[] => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (!stored) return [];
      
      const parsed = JSON.parse(stored) as ExporterFavorite[];
      // Deserialize dates in values and filter out invalid entries
      const validFavorites = parsed
        .filter((fav) => fav && fav.id && fav.name && fav.values)
        .map((fav) => ({
          ...fav,
          values: deserializeValues(fav.values),
        }));
      
      return validFavorites;
    } catch {
      // Clear corrupted data
      localStorage.removeItem(storageKey);
      return [];
    }
  }, [storageKey]);

  const [favorites, setFavorites] = useState<ExporterFavorite[]>(loadFavorites);

  const saveFavorites = useCallback(
    (newFavorites: ExporterFavorite[]) => {
      // Serialize dates before saving
      const serialized = newFavorites.map((fav) => ({
        ...fav,
        values: serializeValues(fav.values),
      }));
      localStorage.setItem(storageKey, JSON.stringify(serialized));
      setFavorites(newFavorites);
    },
    [storageKey],
  );

  const addFavorite = useCallback(
    (name: string, values: IReducedAnswerState) => {
      // Filter out date fields before saving
      const filteredValues = filterDateFields(values);
      const newFavorite: ExporterFavorite = {
        id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        name,
        values: filteredValues,
        createdAt: new Date().toISOString(),
      };
      const newFavorites = [...favorites, newFavorite];
      saveFavorites(newFavorites);
      return newFavorite;
    },
    [favorites, saveFavorites],
  );

  const removeFavorite = useCallback(
    (id: string) => {
      const newFavorites = favorites.filter((f) => f.id !== id);
      saveFavorites(newFavorites);
    },
    [favorites, saveFavorites],
  );

  const findMatchingFavorite = useCallback(
    (currentValues: IReducedAnswerState): ExporterFavorite | null => {
      if (!currentValues || Object.keys(currentValues).length === 0) {
        return null;
      }

      // Filter out date fields and extract only the 'values' property for comparison
      const extractValues = (state: IReducedAnswerState): Record<string, any> => {
        const result: Record<string, any> = {};
        const filtered = filterDateFields(state);
        Object.entries(filtered).forEach(([key, answer]) => {
          if (answer?.values !== undefined) {
            result[key] = answer.values;
          }
        });
        return result;
      };

      const currentValuesOnly = extractValues(currentValues);
      const currentJson = JSON.stringify(currentValuesOnly);

      return (
        favorites.find((favorite) => {
          const favoriteValuesOnly = extractValues(favorite.values);
          const favoriteJson = JSON.stringify(favoriteValuesOnly);
          return currentJson === favoriteJson;
        }) || null
      );
    },
    [favorites],
  );

  return {
    favorites,
    addFavorite,
    removeFavorite,
    findMatchingFavorite,
  };
}
